import React from "react";

export interface SpacerProps {
  /**
   * Optional height. Accepts a number or a numeric string.
   * Will always be converted to integer pixels (e.g. 8 -> "8px", "8" -> "8px").
   * Non-numeric values are ignored and a warning is logged.
   */
  height?: number | string;

  /**
   * Additional inline style (merged). Note: height (if provided) will override
   * any height in this style object.
   */
  style?: React.CSSProperties;

  /**
   * Extra class names
   */
  className?: string;

  /**
   * Optional role/aria attributes — default aria-hidden to true since spacer is visual only
   */
  "aria-label"?: string;
}

/**
 * Spacer - a transparent div used to add vertical spacing.
 * If `height` is provided it will be strictly interpreted as pixels.
 */
export const Spacer: React.FC<SpacerProps> = ({
  height,
  style,
  className,
  "aria-label": ariaLabel,
}) => {
  let heightStyle: React.CSSProperties = {};

  if (height !== undefined && height !== null) {
    // Accept number or numeric string. Force integer px.
    const numeric =
      typeof height === "number" ? height : parseFloat(String(height));
    if (Number.isFinite(numeric) && !Number.isNaN(numeric)) {
      // Use Math.max to avoid negative values, and Math.floor to enforce px integer
      const px = `${Math.max(0, Math.floor(numeric))}px`;
      heightStyle.height = px;
      heightStyle.minHeight = px; // ensure it doesn't collapse
      heightStyle.lineHeight = px; // keep layout predictable if needed
    } else {
      // Non-numeric input — warn and render flexible spacer (no explicit height)
      // eslint-disable-next-line no-console
      console.warn(
        "[Spacer] ignoring non-numeric height value:",
        height,
        " — expected number or numeric string (px)."
      );
    }
  }

  // Default to transparent block that can expand if no height provided.
  const combinedStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    background: "transparent",
    ...heightStyle,
    ...style, // user style merges last (but height already set above)
  };

  return (
    <div
      className={className}
      style={combinedStyle}
      aria-hidden={ariaLabel ? undefined : true}
      aria-label={ariaLabel}
    />
  );
};

export default Spacer;
